/**
 * The UI surface a client-side task engagement event came from. Every client
 * task engagement event (`task_marked_read`, `task_completed`, `task_deferred`,
 * `task_deleted`, `task_list_reordered`, `task_create_clicked`,
 * `task_kanban_task_added`, `task_scheduled_via_drag`,
 * `evaluation_improvement_added_as_task`) carries this ONE property under the
 * same name, so a PostHog Action grouping them breaks down by `surface`.
 * Pure module, safe to import from server and client code.
 *
 * - `sidebar`: the Tasks to Focus On panel, including its Deferred and
 *   Suggested sections (and the collaborator's task panel).
 * - `board_date` / `board_dimension`: the All tasks board, by its mode.
 * - `canvas_card`: task pills on canvas blocks.
 * - `lightbox`: an action taken inside the open task card.
 * - `eval_page`: the evaluation lightbox's task list.
 * - `chat`: Lev chat.
 * - `notification`: the bell's task notification opened the card.
 * - `deep_link`: a task URL hash (shared link, back/forward) opened the card.
 * - `unknown`: an unrecognized value; kept so those rows still count.
 */
export type TaskSurface =
  | "sidebar"
  | "board_date"
  | "board_dimension"
  | "canvas_card"
  | "lightbox"
  | "eval_page"
  | "chat"
  | "notification"
  | "deep_link"
  | "unknown";

const TASK_SURFACES: ReadonlySet<string> = new Set<TaskSurface>([
  "sidebar",
  "board_date",
  "board_dimension",
  "canvas_card",
  "lightbox",
  "eval_page",
  "chat",
  "notification",
  "deep_link",
  "unknown",
]);

export function normalizeTaskSurface(value: unknown): TaskSurface {
  return typeof value === "string" && TASK_SURFACES.has(value)
    ? (value as TaskSurface)
    : "unknown";
}

/** The All tasks board's surface for its current mode. */
export function boardTaskSurface(mode: "date" | "dimension"): TaskSurface {
  return mode === "date" ? "board_date" : "board_dimension";
}
